import fb from '../firebase'
import firebase from 'firebase/compat/app'

interface FirebasePresenceConfig {
  uid: string,
  castId?: string|undefined,
  logCb?: (msg: string, level: 'info'|'warning'|'error') => void,
  sessionId: string
}

interface RTDBStatus {
  uid: string,
  state: 'online' | 'offline',
  last_changed: any,
  castids: string[]
}

class FirebasePresence {
  uid: string
  castIds: string[]
  logCb: ((msg: string, level: 'info'|'warning'|'error') => void)|null
  sessionId: string
  userStatusFirestoreRef: firebase.firestore.DocumentReference<firebase.firestore.DocumentData>
  userStatusDatabaseRef: firebase.database.Reference

  isOfflineForDatabase: RTDBStatus
  isOnlineForDatabase: RTDBStatus

  private authStateUnsubscribe: (() => void)|null = null
  private connectedRef: firebase.database.Reference|null = null

  constructor (config: FirebasePresenceConfig) {
    this.uid = config.uid
    this.castIds = config.castId !== undefined ? [config.castId] : []
    this.sessionId = config.sessionId
    this.userStatusFirestoreRef = fb.db.doc('/session/' + this.sessionId)
    this.userStatusDatabaseRef = fb.rtdb.ref('/sessions/' + this.sessionId)
    this.isOfflineForDatabase = {
      uid: this.uid,
      state: 'offline',
      last_changed: fb.fieldValues.rtdbServerTimestamp,
      castids: this.castIds
    }
    this.isOnlineForDatabase = {
      uid: this.uid,
      castids: this.castIds,
      state: 'online',
      last_changed: fb.fieldValues.rtdbServerTimestamp
    }
    this.logCb = config.logCb ?? null

    // setup our connection
    try {
      this.setupPresence()
    } catch (error) {
      console.error(error)
      setTimeout(() => this.setupPresence(), 4000)
    }
  }

  setupPresence () {
    console.log('** setupPresence called **')

    // Clean up existing listeners before registering new ones
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe()
      this.authStateUnsubscribe = null
    }
    if (this.connectedRef) {
      this.connectedRef.off('value')
      this.connectedRef = null
    }
    
    try {
      this.authStateUnsubscribe = fb.auth.onAuthStateChanged((user) => {
      console.log('** setupPresence onAuthStateChanged called check user:', user)
        if (user) {
          console.log('** setupPresence onAuthStateChanged called user exists:', user)
          this.logCb?.(`RTDB onAuthStateChanged(${user.uid}) [${this.uid} / ${this.sessionId}]`, 'info')
          // Firestore uses a different server timestamp value, so we'll
          // create two more constants for Firestore state.
          const isOfflineForFirestore = {
            state: 'offline',
            last_changed: fb.fieldValues.serverTimestamp()
          }

          const isOnlineForFirestore = {
            state: 'online',
            last_changed: fb.fieldValues.serverTimestamp()
          }

          // Create a reference to the special '.info/connected' path in
          // Realtime Database. This path returns `true` when connected
          // and `false` when disconnected.
          // Clean up any existing connected listener before adding a new one
          if (this.connectedRef) {
            this.connectedRef.off('value')
          }
          this.connectedRef = fb.rtdb.ref('.info/connected')
          this.connectedRef.on('value', (snapshot) => {
            console.log('** info/connected called: snapshot', snapshot)
            console.log('** info/connected logCb message:', `RTDB: .info/connected ${snapshot.val()} [${this.uid} / ${this.sessionId}]`, 'info')
            this.logCb?.(`RTDB: .info/connected ${snapshot.val()} [${this.uid} / ${this.sessionId}]`, 'info')
            if (snapshot.val() === false) {
              console.log('** info/connected snapshot val is false')
              const msg = `RTDB: Set offline ${JSON.stringify(isOfflineForFirestore)} [${this.uid} / ${this.sessionId}]`
              console.log('** info/connected snapshot val is false, logCb msg:', msg)
              this.logCb?.(msg, 'info')
              // TEMP Try to log via Sentry as well in case cloud functions aren't available anymore.
              console.error(msg)
              // Instead of simply returning, we'll also set Firestore's state
              // to 'offline'. This ensures that our Firestore cache is aware
              // of the switch to 'offline.'
              this.userStatusFirestoreRef.set(isOfflineForFirestore)
                .catch((error) => console.error('Could not set user status', error))
              return
            }

            console.log('** info/connected snapshot val is true')

            // If we are currently connected, then use the 'onDisconnect()'
            // method to add a set which will only trigger once this
            // client has disconnected by closing the app,
            // losing internet, or any other means.
            this.userStatusDatabaseRef.onDisconnect().set(this.isOfflineForDatabase).then(() => {
              const a = this.isOfflineForDatabase
              console.log('** info/connected snapshot val is true, onDisconnect, setting isOfflineForDatabase:', a)
              console.log('** info/connected snapshot val is true, logCb msg:', `RTDB: Set online ${JSON.stringify(this.isOnlineForDatabase)} ${JSON.stringify(isOnlineForFirestore)}`
                  + ` [${this.uid} / ${this.sessionId}]`,
                'info')
              this.logCb?.(
                `RTDB: Set online ${JSON.stringify(this.isOnlineForDatabase)} ${JSON.stringify(isOnlineForFirestore)}`
                  + ` [${this.uid} / ${this.sessionId}]`,
                'info'
              )
              // The promise returned from .onDisconnect().set() will
              // resolve as soon as the server acknowledges the onDisconnect()
              // request, NOT once we've actually disconnected:
              // https://fb.google.com/docs/reference/js/fb.database.OnDisconnect

              // We can now safely set ourselves as 'online' knowing that the
              // server will mark us as offline once we lose connection.
              this.userStatusDatabaseRef.set(this.isOnlineForDatabase)
                .catch((error) => console.error('Could not set online status in db', error))

              // We'll also add Firestore set here for when we come online.
              this.userStatusFirestoreRef.set(isOnlineForFirestore)
                .catch((error) => console.error('Could not set online status', error))
            }).catch((error) => console.error('On disconnect error', error))
          })
        }
      })
    } catch (error) {
      console.error('Could not set presence', error)
    }
  }

  async closeCast () {
    const isLoggedIn = fb.auth.currentUser !== null
    console.log('** closeCast called logCb msg:', `RTDB closeCast ${isLoggedIn} [${this.uid} / ${this.sessionId}]`, 'info')
    this.logCb?.(`RTDB closeCast ${isLoggedIn} [${this.uid} / ${this.sessionId}]`, 'info')
    if (!isLoggedIn) {
      console.log('** closeCast called isLoggedIn is false so just return')
      return // nothing needs to be done here
    }

    const isOfflineForFirestore = {
      state: 'offline',
      last_changed: fb.fieldValues.serverTimestamp()
    }
    try {
      await this.userStatusFirestoreRef.set(isOfflineForFirestore)
      console.log('** closeCast called userStatusFirestoreRef.set(isOfflineForFirestore)')
    } catch (error) {
      console.error('Error in closeCast ', error)
    }

    const isOfflineForDatabase = {
      uid: this.uid,
      castids: this.castIds,
      state: 'offline',
      last_changed: fb.fieldValues.rtdbServerTimestamp
    }
    try {
      console.log('** close cast called set isOfflineForDatabase', isOfflineForDatabase)
      await this.userStatusDatabaseRef.set(isOfflineForDatabase)
    } catch (error) {
      console.error('Error in closeCast ', error)
    }
    console.log('Close presence', isOfflineForDatabase, isOfflineForFirestore)
  }

  protected updateCastsPresence () {
    console.log('** updatecast presence called')
    const update = {
      uid: this.uid,
      castids: this.castIds,
      state: 'online',
      last_changed: fb.fieldValues.rtdbServerTimestamp
    }
    this.userStatusDatabaseRef.set(update).catch(error => {
      console.error('Could not update cast info in firebasepresence', error)
    })
    this.isOfflineForDatabase.castids = this.castIds
    this.isOnlineForDatabase.castids = this.castIds
    console.log('** updatecast presence setupPresence is called next')
    this.setupPresence()
  }

  addCast (castId: string) {
    this.logCb?.(`RTDB addCast ${castId} [${this.uid} / ${this.sessionId}]`, 'info')
    if (this.castIds.includes(castId)) return
    this.castIds.push(castId)
    this.updateCastsPresence()
  }

  removeCast (castId: string) {
    console.log('** removeCast called logCb msg:', `RTDB removeCast ${castId} [${this.uid} / ${this.sessionId}]`, 'info')
    this.logCb?.(`RTDB removeCast ${castId} [${this.uid} / ${this.sessionId}]`, 'info')
    if (!this.castIds.includes(castId)) return
    this.castIds = this.castIds.filter((cid) => cid !== castId)
    this.updateCastsPresence()
  }

  async reconnect () {
    console.log('** reconnect called')
    console.log('** reconnect called logCb msg:', `RTDB reconnect [${this.uid} / ${this.sessionId}]`, 'info')
    this.logCb?.(`RTDB reconnect [${this.uid} / ${this.sessionId}]`, 'info')
    console.info('Current time: ' + new Date().toISOString() + ' RTDB reconnect - going offline')
    await fb.rtdb.goOffline()
    console.info('Current time: '+ new Date().toISOString() + ' RTDB reconnect - going online')
    await fb.rtdb.goOnline()
    console.info('Current time: ' + new Date().toISOString() + 'RTDB reconnect - done')
    this.updateCastsPresence()
    console.log('** reconnect done')
  }
}

//
export default FirebasePresence
